/**
 * @budgeting101/i18n — Token-based string lookup
 *
 * Usage:
 *   import { t } from '@budgeting101/i18n';
 *   t('nav.dashboard')          // → 'Dashboard'
 *   t('record.budgetBlockedDetail', { budgetLabel: 'Travel' })
 *
 * RTL: swap locale via setLocale(); direction metadata is exposed per locale.
 */

import { en } from './en.js';
import { ar } from './ar.js';

export type Locale = 'en' | 'ar';
type WidenStrings<T> = { [K in keyof T]: T[K] extends string ? string : WidenStrings<T[K]> };
export type StringTree = WidenStrings<typeof en>;

let _locale: Locale = 'en';
const _locales: Record<Locale, StringTree> = { en, ar };

export function isLocale(value: unknown): value is Locale {
  return value === 'en' || value === 'ar';
}

export function directionForLocale(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

export function setLocale(locale: Locale): void {
  _locale = locale;
}

export function getLocale(): Locale {
  return _locale;
}

/**
 * Resolve a dot-separated key path from the string tree, or null when absent.
 */
function resolveOrNull(tree: unknown, keys: string[]): string | null {
  let node: unknown = tree;
  for (const key of keys) {
    if (typeof node !== 'object' || node === null) return null;
    node = (node as Record<string, unknown>)[key];
  }
  return typeof node === 'string' ? node : null;
}

/**
 * Resolve a dot-separated key path from the string tree.
 * e.g. 'nav.dashboard' → 'Dashboard'
 */
function resolve(tree: unknown, keys: string[]): string {
  return resolveOrNull(tree, keys) ?? keys.join('.');
}

/**
 * Interpolate {{key}} placeholders.
 */
function interpolate(str: string, vars: Record<string, string | number>): string {
  return str.replace(/\{\{(\w+)\}\}/g, (_, key: string) =>
    key in vars ? String(vars[key]) : `{{${key}}}`,
  );
}

/**
 * Translate a dot-path key with optional interpolation variables.
 *
 * @param key  Dot-separated path, e.g. 'nav.dashboard'
 * @param vars Optional interpolation values for {{key}} placeholders
 */
export function t(
  key: string,
  vars?: Record<string, string | number>,
): string {
  const strings = _locales[_locale] ?? _locales.en;
  const raw = resolve(strings, key.split('.'));
  return vars ? interpolate(raw, vars) : raw;
}

/**
 * Translate for an explicit locale, without touching module state.
 *
 * `t()` reads the module-global `_locale`, which is correct in a browser (one
 * user per process) but unsafe on a server rendering for many users at once —
 * two concurrent requests would race the same variable. Server-side callers
 * (push notification bodies) must use this instead.
 *
 * Unlike `t()`, a key missing from the requested locale falls back to English
 * before falling back to the key path. `ar` is a partial catalogue, and a raw
 * dot-path is acceptable as a visible UI glitch but not as the text of a push
 * notification on someone's lock screen.
 */
export function tFor(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const keys = key.split('.');
  const raw = resolveOrNull(_locales[locale] ?? _locales.en, keys)
    ?? resolveOrNull(_locales.en, keys)
    ?? keys.join('.');
  return vars ? interpolate(raw, vars) : raw;
}

// Re-export the en strings for direct access
export { en, ar };
export type { StringKeys } from './en.js';
